import {formatPrice} from '../hooks/generalHooks';
import SERVER from '../hooks/useServer';
import { GoCheckCircleFill } from "react-icons/go";
import { useEffect, useRef, useState } from 'react';
import { IoClose } from "react-icons/io5";
import { useTranslation } from 'react-i18next';


const CartModal = ({ isOpen, onClose, cart, updateQuantity }) => {
    const { t } = useTranslation();
    const modalRef = useRef(null);
    const [visible, setVisible] = useState(false);
    const [animateIn, setAnimateIn] = useState(false);
    const [quantities, setQuantities] = useState({});

    useEffect(() => {
        if (cart && cart.length > 0) {
            const initialQuantities = {};
            cart.forEach(item => {
                initialQuantities[item.id] = item.quantity || 1;
            });
            setQuantities(initialQuantities);
        }
    }, [cart]);

    useEffect(() => {
        if (isOpen) {
            setVisible(true);
            setTimeout(() => setAnimateIn(true), 10);
        } else {
            setAnimateIn(false);
            setTimeout(() => setVisible(false), 300);
        }
    }, [isOpen]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                onClose();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [onClose]);

    const handleQuantityChange = (productId, newQuantity) => {
        if (newQuantity < 1) return;
        setQuantities(prev => ({
            ...prev,
            [productId]: newQuantity
        }));
        updateQuantity(productId, newQuantity)
    };

    if (!isOpen && !visible) return null;
    if (!cart || cart.length === 0) return null;

    // Calculate totals
    const calculateSubtotal = () => {
        return cart.reduce((total, product) => {
            const price = product.promotion > 0 
                ? product.price * (1 - product.promotion / 100)
                : product.price;
            return total + (price * product.quantity);
        }, 0);
    };

    const calculateCalibrationTotal = () => {
        return cart.reduce((total, product) => {
            return total + (parseFloat(product?.calibrage || 0) * product.quantity);
        }, 0);
    };

    const calculateShippingTotal = () => {
        // You can add shipping calculation logic here
        return 7.00; // Example shipping cost
    };

    const calculateGrandTotal = () => {
        return calculateSubtotal() + calculateCalibrationTotal() + calculateShippingTotal();
    };

    return (
        <div className={`fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity duration-300 ${animateIn ? 'opacity-100' : 'opacity-0'}`}>
            <div 
                ref={modalRef}
                className={`fixed right-0 top-0 h-full w-full max-w-lg bg-white shadow-2xl transform transition-transform duration-300 ${animateIn ? 'translate-x-0' : 'translate-x-full'}`}
            >
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <h2 className="text-2xl font-bold text-black">{t('cart.myCart')}</h2>
                    <button 
                        onClick={onClose}
                        className="text-gray-500 hover:text-black transition-colors"
                    >
                        <IoClose size={32} />
                    </button>
                </div>

                {/* Content */}
                <div className="flex flex-col h-full overflow-y-auto pb-48">
                    {/* Success Message */}
                    <div className="p-6 bg-blue-50 border-b border-gray-200">
                        <div className="flex items-center gap-3">
                            <GoCheckCircleFill className="text-primary flex-shrink-0" size={24} />
                            <span className="text-black font-medium">
                                {cart.length} {cart.length > 1 ? t('cart.items') : t('cart.item')} {t('cart.addedToCart')}
                            </span>
                        </div>
                    </div>

                    {/* Products List */}
                    <div className="p-6 space-y-6">
                        {cart.map(product => {
                            const productPrice = product.promotion > 0 
                                ? product.price * (1 - product.promotion / 100)
                                : product.price;
                            
                            return (
                                <div key={product.id} className="space-y-4">
                                    {/* Product Card */}
                                    <div className="flex gap-4">
                                        {/* Product Image */}
                                        <div className="w-40 h-40 rounded-lg overflow-hidden flex-shrink-0 shadow-lg">
                                            <img 
                                                src={`${SERVER}/${product.banner_image}`} 
                                                alt={product.title}
                                                className="w-full h-full object-contain p-2"
                                            />
                                        </div>

                                        {/* Product Info */}
                                        <div className="flex-1 space-y-1">
                                            <h3 className="text-black font-normal text-xl max-w-[200px] leading-tight">
                                                {product.title}
                                            </h3>
                                            
                                            {/* Price */}
                                            <div className="flex items-center gap-2">
                                                <span className="text-primary text-xl font-bold">
                                                    € {formatPrice(productPrice)}
                                                </span>
                                                {product.promotion > 0 && (
                                                    <span className="text-gray-400 line-through text-sm">
                                                        € {formatPrice(product.price)}
                                                    </span>
                                                )}
                                            </div>

                                            {/* Quantity Selector */}
                                            <div className="space-y-1">
                                                <span className="text-gray-600 text-sm">{t('cart.quantity')}</span>
                                                <div className="flex items-center gap-2 border border-gray-300 rounded-lg w-fit">
                                                    <button 
                                                        onClick={() => handleQuantityChange(product.id, quantities[product.id] - 1)}
                                                        disabled={quantities[product.id] <= 1}
                                                        className="w-10 h-10 flex items-center justify-center text-black hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed text-xl font-semibold transition-colors"
                                                    >
                                                        -
                                                    </button>
                                                    <span className="w-8 text-center font-semibold text-black">
                                                        {quantities[product.id] || 1}
                                                    </span>
                                                    <button 
                                                        onClick={() => handleQuantityChange(product.id, quantities[product.id] + 1)}
                                                        className="w-10 h-10 flex items-center justify-center text-black hover:bg-gray-100 text-xl font-semibold transition-colors"
                                                    >
                                                        +
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Product Details - Calibration and Shipping */}
                                    <div className="rounded-lg p-4 space-y-3">
                                        {/* Calibration if exists */}
                                        {product?.calibrage && parseFloat(product.calibrage) > 0 && (
                                            <>
                                                <div className="flex items-center justify-between text-black">
                                                    <span className="text-base">{t('cart.calibration')}</span>
                                                    <span className="text-base font-normal">
                                                        € {formatPrice(parseFloat(product.calibrage) * product.quantity)}
                                                    </span>
                                                </div>
                                                {/* Divider after calibration */}
                                                <div className="border-t border-gray-300"></div>
                                            </>
                                        )}

                                        {/* Shipping per product */}
                                        <div className="flex items-center justify-between text-black">
                                            <span className="text-base">{t('cart.shippingFees')}</span>
                                            <span className="text-base font-normal">
                                                € {formatPrice(calculateShippingTotal() / cart.length)}
                                            </span>
                                        </div>

                                        {/* Divider */}
                                        <div className="border-t border-gray-300 pt-3"></div>

                                        {/* Product Total */}
                                        <div className="flex items-center justify-between">
                                            <span className="text-black text-xl font-bold">{t('cart.total')} :</span>
                                            <span className="text-black text-xl font-bold">
                                                € {formatPrice(
                                                    (productPrice * product.quantity) + 
                                                    (parseFloat(product?.calibrage || 0) * product.quantity) + 
                                                    (calculateShippingTotal() / cart.length)
                                                )}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Divider between products */}
                                    <div className="border-t border-gray-200"></div>
                                </div>
                            );
                        })}
                    </div>

                    
                </div>

                {/* Actions - Fixed at bottom */}
                <div className="absolute bottom-0 left-0 right-0 p-6 bg-white border-t border-gray-200 space-y-3">
                    <button 
                        onClick={() => window.location.href = "/panier"}
                        className="w-full bg-primary text-white py-3 rounded-lg font-normal hover:bg-blue-700 transition-colors text-lg"
                    >
                        {t('cart.confirmOrder')}
                    </button>
                    <button 
                        onClick={onClose}
                        className="w-full bg-white text-black border border-black py-3 rounded-lg font-normal hover:bg-gray-50 transition-colors text-lg"
                    >
                        {t('cart.continueShopping')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CartModal;